/**
 * A small toast layer matching the design (bottom-centre card with a coloured
 * accent edge, an Oxygen icon, and a message; auto-dismisses). It is composed from
 * Oxygen `Icon`s + semantic theme tokens rather than `@8x8/oxygen-toaster`, because
 * that package still pulls a legacy `react-pose` / `react-dom@16` toolchain that is
 * incompatible with the React 18 runtime this app uses. The surface is purely token
 * styled, so it themes for light and dark.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';
import { CheckCircleIcon, ErrorIcon, InfoCircleIcon } from '@8x8/oxygen-icon';

export type ToastKind = 'success' | 'error' | 'info';

interface ToastState {
    kind: ToastKind;
    message: string;
    /** Monotonic id so repeated identical messages still re-trigger the animation. */
    seq: number;
}

interface ToastApi {
    notify: (kind: ToastKind, message: string) => void;
}

const ToastContext = createContext<ToastApi>({ notify: () => undefined });

/** Fire a toast from anywhere inside the provider. */
export function useToast(): ToastApi {
    return useContext(ToastContext);
}

const Anchor = styled.div`
    position: fixed;
    bottom: 24px;
    left: 50%;
    z-index: 1000;
    pointer-events: none;
    animation: ox-toast-in 0.22s ease;
`;

const Card = styled.div<{ $accent: string }>`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 280px;
    max-width: 460px;
    padding: 11px 16px;
    background: ${p => p.theme.ui06};
    color: ${p => p.theme.textColor01};
    border: 1px solid ${p => p.theme.ui01};
    border-left: 3px solid ${p => p.$accent};
    border-radius: 8px;
    box-shadow: 0 6px 24px -6px rgba(0, 0, 0, 0.28);
    font-size: 14px;
    font-weight: 500;
    pointer-events: auto;
`;

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
    const [toast, setToast] = useState<ToastState | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const seq = useRef(0);

    const notify = useCallback((kind: ToastKind, message: string) => {
        seq.current += 1;
        setToast({ kind, message, seq: seq.current });

        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => setToast(null), 2800);
    }, []);

    const api = useMemo(() => ({ notify }), [notify]);

    return (
        <ToastContext.Provider value={api}>
            {children}
            {toast ? <ToastView key={toast.seq} kind={toast.kind} message={toast.message} /> : null}
        </ToastContext.Provider>
    );
}

function ToastView({ kind, message }: { kind: ToastKind; message: string }): JSX.Element {
    const theme = useTheme();
    const accent = kind === 'success' ? theme.success01 : kind === 'error' ? theme.error01 : theme.action08;
    const Glyph = kind === 'success' ? CheckCircleIcon : kind === 'error' ? ErrorIcon : InfoCircleIcon;

    return (
        <Anchor role="status" aria-live="polite">
            <Card $accent={accent}>
                <span style={{ display: 'inline-flex', flexShrink: 0, color: accent }}>
                    <Glyph size={18} />
                </span>
                <span>{message}</span>
            </Card>
        </Anchor>
    );
}
